<template>
    <div class="table-responsive">
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <b-thead>
                <b-tr>
                    <b-th>Estimate:</b-th>
                    <b-th>Title:</b-th>
                    <b-th>Client:</b-th>
                    <b-th>Items:</b-th>
                    <b-th>Total:</b-th>
                    <b-th>Created:</b-th>
                    <b-th>Valid Until:</b-th>
                    <b-th>Status:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="estimate in estimates" :key="estimate.id">
                    <b-td class="text-nowrap">
                        <estimate-link
                            :estimate="estimate"
                            :useRouter="USE_ESTIMATE_MODULE_VUE"
                        />
                    </b-td>
                    <b-td>{{ estimate.title }}</b-td>
                    <b-td>
                        <span v-if="isBusinessActorActive(estimate.client)">
                            <client-name
                                :client="estimate.client"
                                :text="estimate.client.display_name"
                            />
                        </span>
                        <span v-else-if="estimate.client">
                            <client-name
                                :client="estimate.client"
                                :text="estimate.client.display_name"
                                :noUrl="true"
                            />
                        </span>
                    </b-td>
                    <b-td>{{ estimate.item_count }}</b-td>
                    <b-td>{{ estimate.total_amount_estimate }}</b-td>
                    <b-td class="text-nowrap">{{
                        _carbonDateDisplay(estimate.create_at_carbonized)
                    }}</b-td>
                    <b-td class="text-nowrap">{{
                        _carbonDateDisplay(estimate.expire_carbonized)
                    }}</b-td>
                    <b-td
                        >{{ estimate.approve_status }}
                        <span
                            class="text-nowrap"
                            v-if="estimate.approve_status == 'Approved'"
                            >( {{ estimate.approve_at }} )</span
                        ><span
                            class="text-nowrap"
                            v-else-if="estimate.approve_status == 'Converted'"
                            >( {{ estimate.convert_at }} )</span
                        ></b-td
                    >

                    <b-td class="text-right">
                        <spinner v-if="estimate.isLoading" />
                        <b-dropdown
                            variant="link"
                            toggle-class="text-decoration-none"
                            no-caret
                            right
                            v-else-if="
                                estimate.isCanApprove ||
                                estimate.isCanConvert ||
                                _hasAccess('billing.estimate.email') ||
                                _hasAccess('billing.estimate.print') ||
                                _hasAccess('billing.estimate.duplicate') ||
                                (_hasAccess('billing.estimate.delete') &&
                                    isBusinessActorActive(estimate.client))
                            "
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>
                            <span>
                                <b-dropdown-item
                                    href="#"
                                    v-if="estimate.isCanApprove"
                                    @click="showApproveModal(estimate)"
                                >
                                    <i class="cus-accept"></i>
                                    Approve Estimate
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    v-if="estimate.isCanConvert"
                                    @click="showConvertModal(estimate)"
                                >
                                    <i class="cus-arrow-refresh"></i>
                                    Convert Estimate
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    @click="showEmailModal(estimate)"
                                    v-if="_hasAccess('billing.estimate.email')"
                                >
                                    <i class="cus-email"></i>
                                    Email Estimate
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    @click="printEstimate(estimate)"
                                    v-if="_hasAccess('billing.estimate.print')"
                                >
                                    <i class="cus-printer"></i>
                                    Print Estimate
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-if="
                                        _hasAccess('billing.estimate.sms') &&
                                        (_isExtensionEnabled(
                                            EXTENSIONS.CODE.CODE_SMS
                                        ) ||
                                            _isExtensionEnabled(
                                                EXTENSIONS.CODE
                                                    .CODE_2_WAY_MESSAGING
                                            ))
                                    "
                                    href="#"
                                    @click="openEstimateSMSModal(estimate)"
                                >
                                    <i class="cus-phone"></i>
                                    Text (SMS) Estimate
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    @click="duplicateEstimate(estimate)"
                                    v-if="
                                        _hasAccess('billing.estimate.duplicate')
                                    "
                                >
                                    <i class="cus-page-copy"></i>
                                    Duplicate Estimate
                                </b-dropdown-item>
                                <b-dropdown-item
                                    href="#"
                                    v-if="
                                        _hasAccess('billing.estimate.delete') &&
                                        isBusinessActorActive(estimate.client)
                                    "
                                    @click="confirmDelete(estimate)"
                                >
                                    <i class="cus-delete"></i>
                                    Delete Estimate
                                </b-dropdown-item>
                            </span>
                        </b-dropdown>
                    </b-td>
                </b-tr>
                <b-tr v-if="estimates.length == 0 && !isLoading">
                    <b-td colspan="8"><i>No records available</i></b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="meta && showPagination"
            v-show="meta.lastPage > 1"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            prev-text="Prev"
            next-text="Next"
            class="vet-pagination"
            :hide-goto-end-buttons="true"
            @change="getEstimates"
        />

        <modal-new-duplicate-estimate
            modal-id="modal-new-duplicate-estimate"
            ref="modal-new-duplicate-estimate"
            @reload="reloadRelated()"
        />
        <modal-delete
            modal-id="modal-delete-estimate"
            :estimate="selectedEstimate"
            @reload="reloadRelated()"
        />
        <modal-approve
            modal-ref="modal-approve-estimate"
            ref="modal-approve-estimate"
            :client="clientDetails"
            :estimate="selectedEstimate"
            @reload="reloadRelated()"
        />
        <modal-convert
            modal-ref="modal-convert-estimate"
            ref="modal-convert-estimate"
            :client="clientDetails"
            :estimate="selectedEstimate"
            :dashboard="true"
            @reload="reloadRelated()"
        />
        <modal-email
            modal-ref="modal-email-estimate"
            ref="modal-email-estimate"
            :estimate="selectedEstimate"
            :client="clientDetails"
            @reload="reloadRelated()"
            @show-loading="
                isLoading => {
                    selectedEstimate.isLoading = isLoading;
                }
            "
        />
        <exportPrintPdf
            ref="exportPrintPdf"
            :estimate="selectedEstimate"
            :client="clientDetails"
            @show-loading="
                isLoading => {
                    selectedEstimate.isLoading = isLoading;
                }
            "
        />
        <estimate-send-SMS-modal ref="modal-estimate-send-sms" />
    </div>
</template>

<script>
/**
 * @name EstimatesList
 * @description List of estimates with status
 * @see apps/application/views/staff/billing/invoice/estimate/list.php
 * @see apps/assets/js/module/billing/estimate.js
 * @see apps/application/models/orm/TblEstimate.php
 * @see apps/application/controllers/billing/estimate.php
 * @see apps/application/views/staff/billing/invoice/wizard.php
 * @see apps/application/views/staff/billing/invoice/create_update.php
 * @see apps/application/views/staff/billing/invoice/create_update_estimate.php
 * @see apps/application/views/staff/billing/invoice/estimate/convert_to_invoice.php
 */

import Loader from "@/components/elements/loader-list";
import ClientName from "@/components/commons/client-name";
import BillingEstimateServices from "@/services/billing/billing-estimate.js";
import ModalNewDuplicateEstimate from "@/components/billing-estimate/estimate-new-duplicate-modal";
import ModalDelete from "@/components/billing-estimate/estimate-delete-modal";
import ModalApprove from "@/components/billing-estimate/estimate-approve-modal";
import ModalConvert from "@/components/billing-estimate/estimate-convert-modal";
import ModalEmail from "@/components/billing-estimate/estimate-email-modal";
import EstimateLink from "@/components/commons/estimate-link";
import EXTENSIONS from "@/constants/extensions";

export default {
    mixins: {},
    components: {
        Loader,
        ClientName,
        ModalNewDuplicateEstimate,
        ModalDelete,
        ModalApprove,
        ModalConvert,
        ModalEmail,
        EstimateLink,
        Spinner: () => import("@/components/elements/spinner"),
        ExportPrintPdf: () =>
            import("@/components/billing-export/export-print-pdf.vue"),
        EstimateSendSMSModal: () =>
            import("@/components/estimate-sms/modal-estimate-send-sms")
    },
    data() {
        return {
            isLoading: false,
            showPagination: false,
            estimates: [],
            meta: null,
            selectedEstimate: { name: null },
            clientDetails: null,
            EXTENSIONS
        };
    },
    props: {
        status: {
            type: Number,
            required: true
        }
    },
    mounted() {
        this.getEstimates();
    },
    methods: {
        isCanApprove(estimate) {
            return (
                this._hasAccess("billing.estimate.approve") &&
                !estimate.isapprove &&
                estimate.item_count > 0 &&
                this.isBusinessActorActive(estimate.client)
            );
        },
        isCanConvert(estimate) {
            return (
                this._hasAccess("billing.estimate.convert") &&
                estimate.isapprove &&
                !estimate.isconvert &&
                this.isBusinessActorActive(estimate.client)
            );
        },
        isBusinessActorActive(client) {
            return (
                client &&
                client.single_business_actor &&
                client.single_business_actor.isactive
            );
        },
        getEstimates(page) {
            let self = this;
            this.isLoading = true;

            BillingEstimateServices.getEstimates(
                this.PER_PAGE,
                page,
                this.status
            )
                .then(res => {
                    this.isLoading = false;
                    self.meta = res.meta;

                    if (self.meta && self.meta.total && self.meta.total > 20) {
                        self.showPagination = true;
                    }

                    self.estimates = res.resources.map(data =>
                        self.prepareDate(data)
                    );
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to get estimate list",
                        "danger"
                    );
                    this.isLoading = false;
                    console.error(error);
                });
        },
        prepareDate(data) {
            data.isLoading = data.false;
            data.approve_at = data.approve_at
                ? this._toDateTime(data.approve_at).toFormat(
                      this._getLuxonFormat()
                  )
                : data.approve_at;
            data.convert_at = data.convert_at
                ? this._toDateTime(data.convert_at).toFormat(
                      this._getLuxonFormat()
                  )
                : data.convert_at;
            data.isCanApprove = this.isCanApprove(data);
            data.isCanConvert = this.isCanConvert(data);
            return data;
        },
        reloadRelated(page) {
            this.getEstimates(page);
        },
        async printEstimate(estimate) {
            let self = this;
            self.selectedEstimate = estimate;
            await this.$nextTick();
            self.$refs.exportPrintPdf.init();
        },
        duplicateEstimate(estimate) {
            this.$refs["modal-new-duplicate-estimate"].open(estimate);
        },
        confirmDelete(estimate) {
            this.selectedEstimate = estimate;
            this.$bvModal.show("modal-delete-estimate");
        },
        async showApproveModal(estimate) {
            this.clientDetails = estimate.client;
            this.selectedEstimate = estimate;
            await this.$nextTick();
            this.$refs["modal-approve-estimate"].open();
        },
        showConvertModal(estimate) {
            this.clientDetails = estimate.client;
            this.selectedEstimate = estimate;
            this.$refs["modal-convert-estimate"].open();
        },
        async showEmailModal(estimate) {
            this.clientDetails = estimate.client;
            this.selectedEstimate = estimate;
            this.$nextTick();
            this.$refs["modal-email-estimate"].open();
        },
        openEstimateSMSModal(estimate) {
            this.selectedEstimate = estimate;
            this.$refs["modal-estimate-send-sms"].open(
                this.selectedEstimate.id
            );
        }
    }
};
</script>
